import { useActiveMember, useOrganization } from 'hooks';
import useAutomaticBreaksEnabled from 'hooks/feature/automatic-breaks/useAutomaticBreaksEnabled/useAutomaticBreaksEnabled';
import { isNil, sortBy } from 'lodash';
import { Duration } from 'luxon';
import { useMemo } from 'react';
import type IPosition from 'types/Position';
import {
  IPositionBreakSettings,
  IPositionBreakSettingsAutomatic,
  IPositionBreakSettingsDefault,
  POSITION_BREAK_SETTINGS_TYPE_DEFAULT,
  POSITION_BREAK_SETTINGS_TYPE_INTERVAL,
  POSITION_BREAK_SETTINGS_TYPE_MINIMUM,
  POSITION_BREAK_SETTINGS_TYPE_STATIC,
  POSITION_BREAK_SETTINGS_TYPES_AUTOMATIC,
  type IPositionBreakSettingsInterval,
  type IPositionBreakSettingsMinimum,
  type IPositionBreakSettingsStatic,
  type TPositionBreakSettingsType,
  type TPositionBreakSettingsTypeAutomatic,
} from 'types/PositionBreakSettings';
import type { Nullable } from 'types/util/Nullable';
import { t } from 'utils/localize';

export const POSITION_BREAK_SETTINGS_DURATION_DEFAULT: number = Duration.fromObject({ minutes: 30 }).as('seconds');
export const POSITION_BREAK_SETTINGS_STATUS_LABEL_DEFAULT = t('Default Mode');
export const POSITION_BREAK_SETTINGS_STATUS_LABEL_INTERVAL = t('Automatic (Interval)');
export const POSITION_BREAK_SETTINGS_STATUS_LABEL_STATIC = t('Automatic (Set Times)');
export const POSITION_BREAK_SETTINGS_STATUS_LABEL_MINIMUM = t('Minimum Breaks');
export const POSITION_BREAK_SETTINGS_STATUS_LABEL_DICT: Record<TPositionBreakSettingsType, string> = {
  [POSITION_BREAK_SETTINGS_TYPE_DEFAULT]: POSITION_BREAK_SETTINGS_STATUS_LABEL_DEFAULT,
  [POSITION_BREAK_SETTINGS_TYPE_INTERVAL]: POSITION_BREAK_SETTINGS_STATUS_LABEL_INTERVAL,
  [POSITION_BREAK_SETTINGS_TYPE_STATIC]: POSITION_BREAK_SETTINGS_STATUS_LABEL_STATIC,
  [POSITION_BREAK_SETTINGS_TYPE_MINIMUM]: POSITION_BREAK_SETTINGS_STATUS_LABEL_MINIMUM,
} as const;
export const POSITION_BREAK_SETTINGS_STATUS_DESCRIPTION_DICT: Record<TPositionBreakSettingsType, string> = {
  [POSITION_BREAK_SETTINGS_TYPE_DEFAULT]: t(
    `Allows the employee to start and stop their own breaks without restriction.`
  ),
  [POSITION_BREAK_SETTINGS_TYPE_INTERVAL]: t(
    `Removes the employee's ability to start a break. Breaks are automatically deducted from their time card if they work more than a minumum number of hours.`
  ),
  [POSITION_BREAK_SETTINGS_TYPE_STATIC]: t(
    `Removes the employee’s ability to start a break. Breaks automatically begin at specified times and last for 30 minutes. Any employee clocked in during those times will have the time deducted from their time card.`
  ),
  [POSITION_BREAK_SETTINGS_TYPE_MINIMUM]: t(
    `Allows the employee to start a break, but prohibits them from ending the break until at least 30 minutes have passed.`
  ),
} as const;

/**
 * Validate if the provided value meets the required range and minute granularity.
 * @param value - The value to validate.
 * @returns boolean - True if valid, false otherwise.
 * @description
 * Validates offset and duration properties.
 * Ensure seconds integer is between 0 and 84600 with minute granularity.
 */
export function validateBreakSettingsIntSeconds(value?: Nullable<number>): boolean {
  return !isNil(value) && value >= 0 && value <= 84600 && value % 60 === 0;
}

export const isPositionBreakSettingsDefault = (
  config?: Nullable<IPositionBreakSettings>
): config is IPositionBreakSettingsDefault => isNil(config);

export const isPositionBreakSettingsAutomatic = (
  config?: Nullable<IPositionBreakSettings>
): config is IPositionBreakSettingsAutomatic => {
  const asConfig = config as IPositionBreakSettingsAutomatic;

  if (isNil(asConfig) || isNil(asConfig.type) || isNil(asConfig.settings) || !Array.isArray(asConfig.settings)) {
    return false;
  }

  const validSettings = asConfig.settings.every((setting) => {
    const bothNil = isNil(setting?.duration) && isNil(setting?.offset);
    const validDuration = isNil(setting?.duration) || validateBreakSettingsIntSeconds(setting.duration);
    const validOffset = isNil(setting?.offset) || validateBreakSettingsIntSeconds(setting.offset);

    return !bothNil && validDuration && validOffset;
  });

  return validSettings && POSITION_BREAK_SETTINGS_TYPES_AUTOMATIC.includes(asConfig.type);
};

export const isPositionBreakSettingsInterval = (
  config?: Nullable<IPositionBreakSettings>
): config is IPositionBreakSettingsInterval => {
  return isPositionBreakSettingsAutomatic(config) && config.type === POSITION_BREAK_SETTINGS_TYPE_INTERVAL;
};

export const isPositionBreakSettingsStatic = (
  config?: Nullable<IPositionBreakSettings>
): config is IPositionBreakSettingsStatic => {
  return isPositionBreakSettingsAutomatic(config) && config.type === POSITION_BREAK_SETTINGS_TYPE_STATIC;
};

export const isPositionBreakSettingsMinimum = (
  config?: Nullable<IPositionBreakSettings>
): config is IPositionBreakSettingsMinimum => {
  const asConfig = config as IPositionBreakSettingsMinimum;

  return (
    !isNil(asConfig) &&
    asConfig.type === POSITION_BREAK_SETTINGS_TYPE_MINIMUM &&
    validateBreakSettingsIntSeconds(asConfig.duration)
  );
};

const isValidPositionBreakSettings = (config?: Nullable<IPositionBreakSettings>): boolean => {
  return isNil(config) || isPositionBreakSettingsAutomatic(config) || isPositionBreakSettingsMinimum(config);
};

export const getPositionBreakSettingsType = (
  config: IPositionBreakSettings,
  /** if the type cannot be determined, return the default */
  softFail = true
): TPositionBreakSettingsType => {
  if (isPositionBreakSettingsDefault(config)) {
    return POSITION_BREAK_SETTINGS_TYPE_DEFAULT;
  }

  if (isPositionBreakSettingsAutomatic(config) || isPositionBreakSettingsMinimum(config)) {
    return config.type;
  }

  if (softFail) {
    return POSITION_BREAK_SETTINGS_TYPE_DEFAULT;
  } else {
    throw new Error('Invalid position break settings');
  }
};

export const positionBreakSettingsFromString = (
  stringified?: Nullable<string>,
  softFail = true
): IPositionBreakSettings => {
  if (isNil(stringified)) {
    return null as IPositionBreakSettingsDefault;
  }

  try {
    const parsed = JSON.parse(stringified);

    if (isPositionBreakSettingsMinimum(parsed)) {
      return parsed;
    }

    if (isPositionBreakSettingsAutomatic(parsed)) {
      const normalizedSettings = parsed.settings.map(({ offset, duration }) => ({
        offset,
        duration: isNil(duration) ? POSITION_BREAK_SETTINGS_DURATION_DEFAULT : duration,
      }));

      // Ensure settings are sorted by offset and duration
      const sortedSettings = sortBy(normalizedSettings, ['offset', 'duration']);

      const result: IPositionBreakSettingsAutomatic = {
        ...parsed,
        settings: sortedSettings,
      };

      if (isPositionBreakSettingsStatic(result) || isPositionBreakSettingsInterval(result)) {
        return result;
      }
    }

    throw new Error('Invalid position break settings');
  } catch (error) {
    if (softFail) {
      // eslint-disable-next-line no-console
      console.error('Failed to parse break settings:', error);
      return null as IPositionBreakSettingsDefault;
    } else {
      throw error;
    }
  }
};

export const positionBreakSettingsToString = (settings: IPositionBreakSettings): Nullable<string> => {
  if (!isValidPositionBreakSettings(settings)) {
    throw new Error('Invalid position break settings');
  }

  if (isPositionBreakSettingsDefault(settings)) {
    return null;
  }

  return JSON.stringify(settings);
};

export const createPositionBreakSettingsDefault = (): IPositionBreakSettingsDefault => null;

export const createPositionBreakSettingsMinimum = (duration: number): IPositionBreakSettingsMinimum => {
  if (!validateBreakSettingsIntSeconds(duration)) {
    throw new Error('Invalid duration');
  }

  return {
    type: POSITION_BREAK_SETTINGS_TYPE_MINIMUM,
    duration,
  };
};

export const createPositionBreakSettingsAutomatic = (
  type: TPositionBreakSettingsTypeAutomatic,
  settings: IPositionBreakSettingsAutomatic['settings']
): IPositionBreakSettingsAutomatic => {
  if (!POSITION_BREAK_SETTINGS_TYPES_AUTOMATIC.includes(type)) {
    throw new Error('Invalid type');
  }

  const normalizedSettings = settings.map(({ offset, duration }) => {
    if (isNil(duration)) {
      duration = POSITION_BREAK_SETTINGS_DURATION_DEFAULT;
    }

    if (!validateBreakSettingsIntSeconds(offset)) {
      throw new Error(`Invalid offset: ${offset}`);
    }

    if (!validateBreakSettingsIntSeconds(duration)) {
      throw new Error(`Invalid duration: ${duration}`);
    }

    return { offset, duration };
  });

  const positionBreakSettings = { type, settings: normalizedSettings };

  if (!isPositionBreakSettingsAutomatic(positionBreakSettings)) {
    throw new Error('Invalid settings');
  }

  return positionBreakSettings;
};

export const createPositionBreakSettingsInterval = (
  settings: IPositionBreakSettingsInterval['settings']
): IPositionBreakSettingsInterval => {
  const positionBreakSettings = createPositionBreakSettingsAutomatic(POSITION_BREAK_SETTINGS_TYPE_INTERVAL, settings);

  // shouldn't be necessary, but guards the returned type from IPositionBreakSettingsAutomatic to IPositionBreakSettingsInterval
  if (!isPositionBreakSettingsInterval(positionBreakSettings)) {
    throw new Error('Invalid positionBreakSettingsInterval');
  }

  return positionBreakSettings;
};

export const createPositionBreakSettingsStatic = (
  settings: IPositionBreakSettingsStatic['settings']
): IPositionBreakSettingsStatic => {
  const positionBreakSettings = createPositionBreakSettingsAutomatic(POSITION_BREAK_SETTINGS_TYPE_STATIC, settings);

  // shouldn't be necessary, but guards the returned type from IPositionBreakSettingsAutomatic to IPositionBreakSettingsStatic
  if (!isPositionBreakSettingsStatic(positionBreakSettings)) {
    throw new Error('Invalid positionBreakSettingsStatic');
  }

  return positionBreakSettings;
};

export const positionBreakSettingsStatusString = (breakSettings: IPositionBreakSettings) => {
  return POSITION_BREAK_SETTINGS_STATUS_LABEL_DICT[breakSettings?.type ?? POSITION_BREAK_SETTINGS_TYPE_DEFAULT];
};

export const positionBreakSettingsStatusStringFromPosition = ({ breakSettings }: Pick<IPosition, 'breakSettings'>) => {
  const breakSettingsObj = positionBreakSettingsFromString(breakSettings);
  return positionBreakSettingsStatusString(breakSettingsObj);
};

export const useEffectiveBreakSettings = () => {
  const positionBreakSettingsEnabled = useAutomaticBreaksEnabled();

  return (positionBreakSettings: IPositionBreakSettings, organizationMinBreakSeconds: number | undefined) => {
    const canGoOnBreak = !isPositionBreakSettingsAutomatic(positionBreakSettings);

    const hasPositionMinBreakSeconds = isPositionBreakSettingsMinimum(positionBreakSettings);
    const positionMinBreaksRequired = positionBreakSettingsEnabled && hasPositionMinBreakSeconds;
    const positionMinBreakDurationMinutes = hasPositionMinBreakSeconds
      ? positionBreakSettings.duration / 60
      : POSITION_BREAK_SETTINGS_DURATION_DEFAULT;

    // Organization based minimum breaks can be removed once the AutoBreaks feature is fully rolled out
    const hasOrganizationMinBreakSeconds = !isNil(organizationMinBreakSeconds) && organizationMinBreakSeconds > 0;
    const organizationMinBreaksRequired = !positionBreakSettingsEnabled && hasOrganizationMinBreakSeconds;
    const organizationMinBreakDurationMinutes = hasOrganizationMinBreakSeconds
      ? organizationMinBreakSeconds / 60
      : POSITION_BREAK_SETTINGS_DURATION_DEFAULT;

    const minBreakDurationMinutes = positionMinBreaksRequired
      ? positionMinBreakDurationMinutes
      : organizationMinBreaksRequired
        ? organizationMinBreakDurationMinutes
        : null;

    const minBreaksRequired =
      !isNil(minBreakDurationMinutes) && (positionMinBreaksRequired || organizationMinBreaksRequired);

    return {
      canGoOnBreak,
      positionBreakSettings,
      minBreaksRequired,
      positionMinBreaksRequired,
      organizationMinBreaksRequired,
      minBreakDurationMinutes,
      positionMinBreakDurationMinutes,
      organizationMinBreakDurationMinutes,
    };
  };
};

export const useActiveMemberEffectiveBreakSettings = () => {
  const organization = useOrganization();
  const activeMember = useActiveMember();
  const getEffectiveBreakSettings = useEffectiveBreakSettings();

  const positionBreakSettings = useMemo(
    () => positionBreakSettingsFromString(activeMember?.position?.breakSettings),
    [activeMember?.position?.breakSettings]
  );

  return useMemo(
    () => getEffectiveBreakSettings(positionBreakSettings, organization?.minBreakSeconds),
    [getEffectiveBreakSettings, positionBreakSettings, organization?.minBreakSeconds]
  );
};
